// hooks/entities/useEntityData.ts
import { useCallback, useEffect, useRef, useState } from 'react';
import {
    getAdtechs,
    getBookingsForCustomer,
    getCampaignsForCustomer,
    getChannels,
    getCreativesForCustomer,
    getImpressionTagsForCustomer,
    getSiteTagsForCustomer,
    getTerritorialContext,
} from '@/api';
import { notifyError } from '@/utils';
import axios from 'axios';
import type { Creative } from '@/types/creatives';
import type { Campaign } from '@/types/campaigns';
import type { BookingResponse } from '@/types/bookings';
import type { Channel, TerritorialContextResponse } from '@/types/channels';
import type { AdTech } from '@/types/adtechs';
import type { SiteTag } from '@/types/site-tags';
import type { ImpressionTag } from '@/types/impression-tags';

interface CustomerContent {
    creativesData: Creative[];
    siteTagsData: SiteTag[];
    campaignsData: Campaign[];
    impressionTagsData: ImpressionTag[];
    bookingsData: BookingResponse[];
    channelsData: Channel[];
    adtechsData: AdTech[];
    territorialContext: TerritorialContextResponse | null;
}

const initialContent: CustomerContent = {
    creativesData: [],
    siteTagsData: [],
    campaignsData: [],
    impressionTagsData: [],
    bookingsData: [],
    channelsData: [],
    adtechsData: [],
    territorialContext: null,
};

export const useEntityData = (selectedCustomerId: number | null) => {
    const [customerContent, setCustomerContent] = useState<CustomerContent>(initialContent);
    const [loading, setLoading] = useState(false);
    // Flips true the first time fetchAllCustomerData completes (success or
    // failure). Consumers use this to render a readiness signal for E2E
    // tests — so they can wait on "initial data has landed" instead of
    // racing the empty-state placeholder.
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const abortControllerRef = useRef<AbortController | null>(null);

    const fetchAllCustomerData = useCallback(async (customerId: number) => {
        abortControllerRef.current?.abort();
        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;

        if (!customerId) return;
        setLoading(true);

        // Every customer service reads only the id
        const customer = { id: customerId };
        try {
            const [creatives, siteTags, campaigns, bookings, impressionTags, channels, adtechs, territorialContext] =
                await Promise.all([
                    getCreativesForCustomer(customer, signal),
                    getSiteTagsForCustomer(customer, signal),
                    getCampaignsForCustomer(customer, signal),
                    getBookingsForCustomer(customer, signal),
                    getImpressionTagsForCustomer(customer, signal),
                    getChannels({ signal }),
                    getAdtechs({ signal }),
                    getTerritorialContext(customer, signal),
                ]);
            // Superseded (a newer fetch, a customer change, unmount): its results are stale
            if (signal.aborted) return;

            setCustomerContent((prev) => ({
                ...prev,
                creativesData: (creatives as Creative[]) || [],
                siteTagsData: (siteTags as SiteTag[]) || [],
                campaignsData: (campaigns as Campaign[]) || [],
                bookingsData: (bookings as BookingResponse[]) || [],
                impressionTagsData: (impressionTags as ImpressionTag[]) || [],
                channelsData: (channels as Channel[]) || [],
                adtechsData: (adtechs as AdTech[]) || [],
                territorialContext: (territorialContext as TerritorialContextResponse) || null,
            }));
        } catch (error) {
            if (!axios.isCancel(error)) {
                console.error('Error fetching customer data:', error);
                notifyError('Unable to retrieve data. Please try again.');
            }
        } finally {
            // Only the current fetch settles the loading state: a superseded one finishing
            // early would flash the empty "No data" state while its successor is in flight
            if (abortControllerRef.current?.signal === signal) {
                setLoading(false);
                setHasLoadedOnce(true);
            }
        }
    }, []);

    const refreshData = useCallback(
        (overrideCustomerId: number | null = null): Promise<void> => {
            const customerId = overrideCustomerId ?? selectedCustomerId;
            if (customerId) {
                console.debug(`Refreshing customer data for ID: ${customerId}...`);
                return fetchAllCustomerData(customerId);
            }
            return Promise.resolve();
        },
        [fetchAllCustomerData, selectedCustomerId]
    );

    // Fetch when the customer changes, and abort in the same effect's cleanup (React: "Fetching
    // data", You Might Not Need an Effect). StrictMode's development double mount then aborts the
    // first request and the remount simply fetches again. The per-customer guard this replaces
    // skipped that second fetch while a separate unmount cleanup had already aborted the first,
    // so under `npm run dev` Setup never received its data (and the booking form, frozen on an
    // empty territorial context, looped). With no customer there is no fetch to settle the
    // loading state, so it is settled here.
    useEffect(() => {
        if (!selectedCustomerId) {
            setLoading(false);
            return;
        }
        void fetchAllCustomerData(selectedCustomerId);
        return () => abortControllerRef.current?.abort();
    }, [selectedCustomerId, fetchAllCustomerData]);

    return { customerContent, refreshData, loading, hasLoadedOnce };
};
